import './index.scss';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Dropdown, Menu, Iconfont, Button, Modal, ButtonType } from '@umu/umu-design';
import { IMenuItemInfo, IMenuProps } from '@umu/umu-design/es/components/Menu/interface';
import debounce from 'lodash/debounce';
import { lang } from '@umu/ukit-lang';
import DepartmentSelectDialog from '../@departmentSelectDialog/departmentSelectDialog';
import { EPlatformTravelType } from '@umu/umu-types';
import { useClickAway, useHover } from 'ahooks';
import { IBatchProps, onBatchAddAccountDialog } from './components/batchAddAccount';
import { ITransferData } from './components/batchAddAccount/transformInput';
import { If } from 'common/src/components/if';
import isArray from 'lodash/isArray';
import { DepartmentSelectDialogWithUSE } from '../departmentSelectDialogWithUSE/departmentSelectDialogWithUSE';
import { TravelType } from '../departmentSelectDialogWithUSE/type';
import { textTransformToCapital } from '@umu/umu-util';

export interface ISearchItem extends IMenuItemInfo {
    id: string;
    info?: Record<string, string>;
    type?: string;
}
interface IProps extends React.HTMLAttributes<HTMLDivElement> {
    placeholder: string;
    preTitle?: string;
    isSePlate?: boolean;
    isSignal?: boolean;
    useId?: string;
    multiple?: boolean;
    batchProps?: IBatchProps;
    deleteKey?: string | string[];
    isAllowShowBatch?: boolean;
    isHideSearch?: boolean;
    isAllowShowDepartment?: boolean;
    departmentText?: string | JSX.Element;
    batchText?: string | JSX.Element;
    debounceTimeout?: number;
    menuProps?: IMenuProps;
    deafultSelectList?: ISearchItem[];
    batchSelectMemberTitle?: string;
    btnConfirmType?: ButtonType;
    selectKey?: string;
    searchKeyWords?: string;
    dropDownProps?: any;
    isAllowKeyWord?: boolean; // 是否允许关键字搜索
    departmentmentDialogClassName?: string;
    onTransformMenuList?: (data: ISearchItem[], actType?: string) => ISearchItem[];
    onPreviewLoadData?: (value: string) => Promise<ITransferData[]>;
    onLoadData: (data: string) => Promise<ISearchItem[]>;
    onSelectChange?: (list: ISearchItem[], isDel?: boolean, keyWords?: string) => void;
}
const { confirm } = Modal;

const SearchInput = (props: IProps) => {
    const {
        placeholder = '',
        useId = '',
        multiple = false,
        isSignal = false,
        selectKey,
        isAllowShowBatch = false,
        isHideSearch = false,
        searchKeyWords = '',
        batchText = textTransformToCapital(lang('批量输入')),
        batchSelectMemberTitle = '',
        departmentText = lang('部门选择'),
        isAllowShowDepartment = false,
        isSePlate = false,
        debounceTimeout = 50,
        className = '',
        menuProps,
        preTitle = '',
        deleteKey = '',
        batchProps = {},
        btnConfirmType = 'primary-blue',
        isAllowKeyWord = false,
        departmentmentDialogClassName,
        deafultSelectList = [],
        dropDownProps = {},
        onLoadData,
        onPreviewLoadData,
        onTransformMenuList,
        onSelectChange,
        ...rest
    } = props;
    const fetchRef = useRef(0);
    const menuRef = useRef<HTMLDivElement>(null);
    const refUl = useRef<HTMLUListElement>(null);
    const [searchWord, setSearchWord] = useState(searchKeyWords);
    const [visible, setVisible] = useState(false);
    const [isLoading, seIsLoading] = useState(false);
    const [list, setList] = useState<ISearchItem[]>([]);
    const [selectList, setSelectList] = useState<ISearchItem[]>(deafultSelectList ?? []);
    const isHoverMenuIng = useHover(menuRef);
    useClickAway(() => {
        if (!isHoverMenuIng) {
            onReset();
        }
    }, refUl);
    useEffect(() => {
        if (searchKeyWords !== searchWord) {
            setSearchWord(searchKeyWords);
        }
    }, [searchKeyWords]);
    useEffect(() => {
        if (deleteKey) {
            const newSelectList = selectList.filter((item) => {
                return isArray(deleteKey) ? !deleteKey.includes(item.key) : item.key !== deleteKey;
            });
            setSelectList(newSelectList);
        }
        if (!selectKey) {
            setSelectList([]);
        }
    }, [deleteKey, selectKey]);
    const onSelectListChange = (newList: ISearchItem[]) => {
        setSelectList(newList);
        onSelectChange?.(newList, false, searchWord);
    };
    const onTransformList = (data: any[], actType?: string) => {
        return onTransformMenuList ? onTransformMenuList(data, actType) : list;
    };
    const onSearch = useMemo(() => {
        const loadOptions = (value: string) => {
            if (value === '' || isLoading) {
                setList([]);
                return;
            }
            fetchRef.current += 1;
            const fetchId = fetchRef.current;
            setList([]);
            seIsLoading(true);

            onLoadData(value).then((list: ISearchItem[]) => {
                if (fetchId !== fetchRef.current) {
                    return;
                }
                setList(onTransformList(list));
                seIsLoading(false);
                setVisible(list.length > 0);
            });
        };
        return debounce(loadOptions, debounceTimeout);
    }, [debounceTimeout, onLoadData]);

    const onInputFocus = () => {
        if (list.length > 0) {
            setVisible(true);
            return;
        }
        !!searchWord && onSearch(searchWord);
    };
    const onSearchWord = (value: string) => {
        setSearchWord(value);
        if (isAllowKeyWord) {
            onSelectChange?.(selectList, false, value);
        }
    };
    const onSearchWordChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        onSearchWord(value);
        onSearch(value);
    };
    const onReset = () => {
        setVisible(false);
        if (!isAllowKeyWord) {
            onSearchWord('');
        }
        setList([]);
    };
    const onDelSelect = (item: ISearchItem) => {
        const newSelectList = selectList.filter((selectItem) => selectItem.key !== item.key);
        onSelectListChange(newSelectList);
    };
    const handleSelectItem = (item: { key: string }) => {
        const { key } = item;
        const info = list.find((item) => item.key === key);
        if (!info) {
            return;
        }

        const isHas = selectList.some((item) => item.key === key);

        if (isHas) {
            onDelSelect(info);
        } else {
            onSelectListChange(isSignal ? [info] : [...selectList, info]);
        }
        if (!multiple) {
            onReset();
            setSearchWord('');
        }
    };
    const onBatchSelectMember = () => {
        const dialog = onBatchAddAccountDialog({
            title: batchSelectMemberTitle,
            dialogProps: {
                className: 'search-input-batch-add-account-dialog',
                wrapClassName: 'search-input-batch-add-account-dialog-wrap',
            },
            batchProps: {
                submitBtnType: btnConfirmType,
                ...batchProps,
                onPreview: (value, callBack) => {
                    onPreviewLoadData &&
                        onPreviewLoadData(value).then(
                            (preList: ITransferData[]) => {
                                callBack(preList || []);
                            },
                            () => {
                                callBack([]);
                            },
                        );
                },
                onSubmit: (batchList: ITransferData[]) => {
                    const addList = batchList.filter(
                        (item) => !selectList.some((selectItem) => selectItem.key === item.key),
                    ) as ISearchItem[];
                    onSelectListChange([...selectList, ...addList]);
                    dialog.destroy();
                },
            },
        });
    };
    /**
     * USE 平台下的部门选择
     */
    const onSelectDepartmentSePlate = (list: ISearchItem[]) => {
        const defaultSelectedDepartmentList = list
            .map((item) => {
                return {
                    id: item.key.split('-')?.[0] || '',
                };
            })
            .filter((item) => item.id);
        const dialog = confirm({
            centered: true,
            width: '900px',
            closable: false,
            zIndex: 99999,
            wrapClassName: departmentmentDialogClassName,
            content: (
                <DepartmentSelectDialogWithUSE
                    isRadio={false}
                    title={lang('选择部门')}
                    departmentId={''}
                    isShowMember={false}
                    btnConfirmType={btnConfirmType}
                    defaultSelectedDepartmentList={defaultSelectedDepartmentList}
                    cbClose={() => dialog.destroy()}
                    excludeDepartmentIds={'0'}
                    cbDo={(data) => {
                        const departmentList = onTransformMenuList
                            ? onTransformMenuList(data, 'department')
                            : data.map((item) => {
                                  return {
                                      ...item,
                                      id: item.department_id,
                                      name: item.department_name || '',
                                      title: item.department_name || '',
                                      account: item.department_name || '',
                                      key: `${item.department_id}-department}`,
                                  };
                              });
                        const addList = departmentList.filter(
                            (item) => !selectList.some((selectItem) => selectItem.key === item.key),
                        );
                        onSelectListChange([...selectList, ...addList]);
                        dialog.destroy();
                    }}
                    useId={useId}
                    isOnlyShowUSESelectedDepartment
                    travelType={TravelType.department}
                />
            ),
            footer: null,
        } as any);
    };
    /**
     * @desc 选择部门
     */
    const onSelectDepartment = () => {
        const departmentList = selectList.filter((item) => item.key.includes('-department'));
        const ids = departmentList.map((item) => item.key.split('-')[0]).join(',');
        if (isSePlate) {
            onSelectDepartmentSePlate(departmentList);
            return;
        }

        const dialog = confirm({
            centered: true,
            width: '900px',
            closable: false,
            zIndex: 99999,
            wrapClassName: departmentmentDialogClassName,
            content: (
                <DepartmentSelectDialog
                    isRadio={false}
                    title={lang('选择部门')}
                    departmentId={''}
                    btnConfirmType={btnConfirmType}
                    travelType={EPlatformTravelType.department}
                    defaultSelectedList={ids}
                    cbClose={() => dialog.destroy()}
                    excludeDepartmentIds={'0'}
                    cbDo={(data) => {
                        const departmentList = onTransformMenuList
                            ? onTransformMenuList(data, 'department')
                            : data.map((item) => {
                                  return {
                                      ...item,
                                      id: item.department_id,
                                      name: item.department_name || '',
                                      account: item.department_name || '',
                                      key: `${item.department_id}-department}`,
                                      title: item.department_name || '',
                                  };
                              });
                        const addList = departmentList.filter(
                            (item) => !selectList.some((selectItem) => selectItem.key === item.key),
                        );
                        onSelectListChange([...selectList, ...addList]);
                        dialog.destroy();
                    }}
                />
            ),
            footer: null,
        } as any);
    };

    const renderSelectList = () => {
        return selectList.map((item) => {
            return (
                <li className="search-member-item display-flex" key={`member-item-${item.key}`}>
                    <div>{item.title as React.ReactNode}</div>
                    <div className="search-member-item-close">
                        <Iconfont type="Close" className="icon-info-arrow-close" onClick={() => onDelSelect(item)} />
                    </div>
                </li>
            );
        });
    };
    const renderContentSuffix = () => {
        if (!isAllowShowDepartment) {
            return '';
        }
        return (
            <div className="add-department-zone display-flex" onClick={onSelectDepartment}>
                <Iconfont type="Department" className="icon-info-department" />
                <div className="department-text">{departmentText}</div>
                <Iconfont type="SmallMore" className="icon-info-arrow-right" />
            </div>
        );
    };

    const onSearchWordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // input 在聚焦状态时，删除键删除最后一个选中的成员
        if (e.key === 'Backspace' && !searchWord && selectList.length > 0) {
            onDelSelect(selectList[selectList.length - 1]);
        }
    };
    const renderContent = () => {
        return (
            <div className={`search-member-zone ${isAllowShowDepartment ? 'with-department' : ''}`}>
                <If condition={!isHideSearch}>
                    <ul className="search-member-list flex--1" ref={refUl}>
                        {renderSelectList()}
                        <li
                            className="search-member-input"
                            style={{ display: isSignal && selectList.length > 0 ? 'none' : '' }}
                        >
                            <input
                                type="text"
                                className="search-member-input-info"
                                value={searchWord}
                                onFocus={onInputFocus}
                                placeholder={selectList?.length ? '' : placeholder}
                                onChange={onSearchWordChange}
                                onKeyDown={onSearchWordKeyDown}
                            />
                        </li>
                    </ul>
                </If>
                {renderContentSuffix()}
            </div>
        );
    };
    return (
        <div className={`course-data-analysis-search-member ${className}`} {...rest}>
            <If condition={isAllowShowBatch}>
                <div className="display-flex">
                    {preTitle && <div>{preTitle}</div>}
                    <Button type="text" className="col-split-left" isOnlyChildren onClick={onBatchSelectMember}>
                        {batchText}
                    </Button>
                </div>
            </If>

            <If condition={!isHideSearch || isAllowShowDepartment}>
                <>
                    {renderContent()}
                    {list.length > 0 && (
                        <Dropdown
                            overlayClassName={`course-data-analysis-search-member-popup-menu-list ${className}-popup-menu-list`}
                            overlay={
                                <div ref={menuRef}>
                                    <Menu
                                        multiple={!isSignal}
                                        inlineIndent={8}
                                        itemClassName={`popup-menu-item ${className}-popup-menu-item`}
                                        list={list}
                                        onClick={handleSelectItem}
                                        {...menuProps}
                                    />
                                </div>
                            }
                            visible={visible}
                            {...dropDownProps}
                        ></Dropdown>
                    )}
                </>
            </If>
        </div>
    );
};

export { SearchInput };
